import { useEffect, useCallback, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';

export const PhotoLightbox = ({ photos, index, title, onClose, onNavigate, autoPlay = false }) => {
  const [isPlaying, setIsPlaying] = useState(autoPlay);

  const prev = useCallback(
    () => onNavigate((index - 1 + photos.length) % photos.length),
    [index, photos.length, onNavigate]
  );
  const next = useCallback(
    () => onNavigate((index + 1) % photos.length),
    [index, photos.length, onNavigate]
  );

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') { setIsPlaying(false); prev(); }
      if (e.key === 'ArrowRight') { setIsPlaying(false); next(); }
      if (e.key === ' ') { e.preventDefault(); setIsPlaying((p) => !p); }
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [prev, next, onClose]);

  useEffect(() => {
    if (!isPlaying || photos.length < 2) return;
    const timer = setInterval(next, 3500);
    return () => clearInterval(timer);
  }, [isPlaying, next, photos.length]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        data-testid="lightbox-close"
        className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/25 transition-colors z-10"
      >
        <X className="w-6 h-6" />
      </button>

      {photos.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIsPlaying(false); prev(); }}
          aria-label="Photo précédente"
          data-testid="lightbox-prev"
          className="absolute left-3 sm:left-6 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/25 transition-colors z-10"
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
      )}

      <img
        src={photos[index]}
        alt={`${title} - photo ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
        className="max-h-[85vh] max-w-[92vw] object-contain rounded-xl shadow-2xl"
      />

      {photos.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIsPlaying(false); next(); }}
          aria-label="Photo suivante"
          data-testid="lightbox-next"
          className="absolute right-3 sm:right-6 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/25 transition-colors z-10"
        >
          <ChevronRight className="w-7 h-7" />
        </button>
      )}

      <div
        className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        {photos.length > 1 && (
          <button
            type="button"
            onClick={() => setIsPlaying((p) => !p)}
            aria-label={isPlaying ? 'Mettre en pause le diaporama' : 'Lancer le diaporama'}
            data-testid="lightbox-play-pause"
            className="w-11 h-11 rounded-full bg-[#D4AF37] text-[#1A252C] flex items-center justify-center hover:bg-[#e6c455] transition-colors shadow-lg"
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>
        )}
        <div
          className="px-4 py-1.5 rounded-full bg-white/10 text-white text-sm"
          data-testid="lightbox-counter"
        >
          {index + 1} / {photos.length}
        </div>
      </div>
    </div>
  );
};

export default PhotoLightbox;
